import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'
import { useCrashDetectionList } from '@/services/crash-detection/useCrashDetectionList'
import {
  useAssignCrashDetectionOwner,
  AssignCrashDetectionOwnerErrorCode,
  AssignCrashDetectionOwnerErrorData,
} from '@/services/crash-detection/useAssignCrashDetectionOwner'
import { useFilterNotificationStatus } from '@/services/crash-detection/useFilterNotificationStatus'
import { useFilterContactStatus } from '@/services/crash-detection/useFilterContactStatus'
import { useFilterIncidentStatus } from '@/services/crash-detection/useFilterIncidentStatus'
import { useFilterUser } from '@/services/crash-detection/useFilterUser'
import CONSTANT from '@/constant'
import {
  CrashContactStatus,
  CrashIncidentStatus,
  CrashUserRespondType,
  CrashDetectionCase,
} from '@/services/crash-detection/types'
import dayjs from 'dayjs'
import { ColumnsType } from 'antd/lib/table'
import { BlinkingWrapper, Dot, ButtonIcon, EmptySpaceIcon } from './styles'
import { PhoneFilled } from '@ant-design/icons'
import { Space, Tooltip, TablePaginationConfig } from 'antd'
import { FilterValue, SorterResult } from 'antd/lib/table/interface'
import { IMAGE_URL } from '@/constant/IMAGE'
import { UserSelectStyle } from '@/components/common/Table/styles'
import { NotificationStatus, NOTIFICATION_STATUS_OPTIONS } from '@/constant/CRASH_DETECTION'
import { toThaiLocaleFullDateAndTime } from '@/libs/date'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import userAtom from '@/recoil/user'
import crashDetectionAtom from '@/recoil/crashDetection'
import crashDetectionLockedModalAtom from '@/recoil/crashDetectionLockedModal'
import { BACKOFFICE_USER_PERMISSION } from '@/constant/BACKOFFICE_PERMISSION'
import { validatePerm } from '@/libs/validatePermission'

const useCrashDetectionTableHook = () => {
  const navigate = useNavigate()
  const user = useRecoilValue(userAtom)
  const hasEditPermission = validatePerm([BACKOFFICE_USER_PERMISSION.CRS010102], user.permissions)
  const setLockedModal = useSetRecoilState(crashDetectionLockedModalAtom)
  const { mutate: assignOwner } = useAssignCrashDetectionOwner()

  // Claim the case before navigating to edit. On success (or idempotent re-claim) the
  // backend returns 204 and we navigate; if another owner holds the 2-hour lock the
  // backend returns BIZBOCRASH1001 and we surface the locked modal instead.
  const handleEditCase = (record: CrashDetectionCase) => {
    assignOwner(record.id, {
      onSuccess: () => {
        navigate(CONSTANT.ROUTES.CRASH_DETECTION_EDIT_PAGE.replace(':id', record.id))
      },
      onError: (error: any) => {
        const errorBody = error?.response?.data
        const code = errorBody?.code
        if (code === AssignCrashDetectionOwnerErrorCode.LOCKED_BY_OTHER_OWNER) {
          // Owner identity comes from the error body's `data`, not the list row.
          const owner = errorBody?.data as AssignCrashDetectionOwnerErrorData | undefined
          setLockedModal({
            isVisible: true,
            errorCode: code,
            ownerName: owner?.ownerName,
            ownerEmployeeCode: owner?.ownerEmployeeCode,
          })
          return
        }
        if (code === AssignCrashDetectionOwnerErrorCode.LOCKED_BY_CURRENT_USER_OTHER_CASE) {
          const owner = errorBody?.data as AssignCrashDetectionOwnerErrorData | undefined
          setLockedModal({
            isVisible: true,
            errorCode: code,
            lockedCrashInfoId: owner?.crashDetectionCrashInfoId,
          })
          return
        }
        if (code === AssignCrashDetectionOwnerErrorCode.CASE_NOT_FOUND) {
          message.error('ไม่พบรายการตรวจจับการชนนี้')
          return
        }
        message.error('เกิดข้อผิดพลาดในการเข้าถึงรายการ')
      },
    })
  }
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [crashDetectionState, setCrashDetectionState] = useRecoilState(crashDetectionAtom)
  const isAutoUpdate = crashDetectionState.isAutoUpdate
  const setIsAutoUpdate = (value: boolean) =>
    setCrashDetectionState((prev) => ({ ...prev, isAutoUpdate: value }))
  const [filters, setFilters] = useState<Record<string, FilterValue | null>>({})
  const [formattedFilters, setFormattedFilters] = useState<string>('')
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)

  const { data, isLoading } = useCrashDetectionList({
    page: currentPage,
    limit: pageSize,
    search: search,
    filter: formattedFilters,
    refetchInterval: isAutoUpdate ? 15000 : false, // 15 seconds auto-refresh
  })

  const queryFilterNotificationStatus = useFilterNotificationStatus()
  const queryFilterContactStatus = useFilterContactStatus()
  const queryFilterIncidentStatus = useFilterIncidentStatus()
  const queryFilterUser = useFilterUser()

  const onSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setSearch(value)
    setCurrentPage(1)
  }, [])

  const handleFilter = (newFilters: Record<string, FilterValue | null>) => {
    const filterAndPrefixList = [
      { key: 'notificationStatus', prefix: 'filter.notificationStatus' },
      { key: 'currentContactStatus', prefix: 'filter.currentContactStatus' },
      { key: 'currentIncidentStatus', prefix: 'filter.currentIncidentStatus' },
      { key: 'lastUpdateUserId', prefix: 'filter.lastUpdateUserId' },
    ]

    const formatted = Object.entries(newFilters)
      .map(([key, value]) => {
        const filterItem = filterAndPrefixList.find((item) => item.key === key)
        if (filterItem && value) {
          const prefix = filterItem.prefix
          if (Array.isArray(value)) {
            const values = value.map(String)
            if (key === 'currentIncidentStatus' && values.includes(CrashIncidentStatus.NO_STATUS)) {
              const otherValues = values.filter((v) => v !== CrashIncidentStatus.NO_STATUS)
              const allValues = [CrashIncidentStatus.NO_STATUS, 'null', ...otherValues]
              return `${prefix}=$in:${allValues.join(',')}`
            }
            return value.map((v, i) => (i === 0 ? `${prefix}=$in:${v}` : String(v))).join(',')
          }
          return `${prefix}=$in:${value}`
        }
        return ''
      })
      .filter(Boolean)
      .join('&')

    setFormattedFilters(formatted)
  }

  const onTableChange = (
    pagination: TablePaginationConfig,
    newFilters: Record<string, FilterValue | null>,
    _sorter: SorterResult<{}> | SorterResult<{}>[],
  ) => {
    handleFilter(newFilters)
    setCurrentPage(pagination.current || 1)
    setPageSize(pagination.pageSize || 10)
    setFilters(newFilters)
  }

  const formatThaiBE = (dateStr: string | Date | undefined) => {
    if (!dateStr) return '-'
    return toThaiLocaleFullDateAndTime(String(dateStr)) || '-'
  }

  const getElapsed = (record: any) => {
    const isNotContacted =
      !record.currentContactStatus ||
      record.currentContactStatus === CrashContactStatus.NOT_CONTACTED
    const isCrash = record.userRespondType === CrashUserRespondType.CRASH
    if (!isCrash || !isNotContacted || !record.userRespondAt) return '-'
    const diff = dayjs().diff(dayjs(record.userRespondAt), 'minute')
    if (diff < 60) return `${diff} นาทีที่แล้ว`
    const hours = Math.floor(diff / 60)
    const mins = diff % 60
    return `${hours} ชม. ${mins > 0 ? `${mins} นาที` : '00 นาที'}`.trim()
  }

  const getNotificationStatus = (record: any) => {
    const userRespondType = record.userRespondType
    if (userRespondType === CrashUserRespondType.CRASH) {
      return NotificationStatus.CONFIRM_CRASH
    }
    if (userRespondType === CrashUserRespondType.NO_CRASH) {
      return NotificationStatus.NO_NEED_HELP
    }

    if (userRespondType === CrashUserRespondType.NO_ACTION) {
      return NotificationStatus.NO_ACTION
    }

    return NotificationStatus.NOTI_SENT
  }

  const columns: ColumnsType<any> = [
    {
      title: 'ลำดับ',
      key: 'order',
      width: 80,
      className: UserSelectStyle,
      render: (_: any, __: any, index: number) => {
        return (currentPage - 1) * pageSize + index + 1
      },
    },
    {
      title: 'สถานะการแจ้งเตือน',
      key: 'notificationStatus',
      width: 250,
      className: UserSelectStyle,
      filters:
        queryFilterNotificationStatus.data?.map((o) => ({
          text: o.name,
          value: o.code ?? 'null',
        })) || [],
      filteredValue: filters['notificationStatus'] || null,
      render: (_: any, record: any) => {
        const statusVal = getNotificationStatus(record)
        const opt = NOTIFICATION_STATUS_OPTIONS.find((o) => o.value === statusVal)
        if (!opt) return '-'

        const isNotContacted =
          !record.currentContactStatus ||
          record.currentContactStatus === CrashContactStatus.NOT_CONTACTED
        const showPhoneIcon = opt.hasPhoneIcon && isNotContacted

        return (
          <Space>
            {showPhoneIcon && (
              <BlinkingWrapper>
                <PhoneFilled style={{ fontSize: '12px', color: opt.color }} />
              </BlinkingWrapper>
            )}
            <Dot color={opt.color} />
            {opt.label}
          </Space>
        )
      },
    },
    {
      title: 'สถานะติดต่อ',
      dataIndex: 'currentContactStatus',
      key: 'currentContactStatus',
      width: 180,
      className: UserSelectStyle,
      filters:
        queryFilterContactStatus.data?.map((o) => ({
          text: o.name,
          value: o.code,
        })) || [],
      filteredValue: filters['currentContactStatus'] || null,
      render: (status: CrashContactStatus) => {
        const currentStatus = status || CrashContactStatus.NOT_CONTACTED
        // map API code to display label from filter data, fallback to static map
        const opt = queryFilterContactStatus.data?.find((o) => o.code === currentStatus)
        const colorMap: Record<string, string> = {
          [CrashContactStatus.CONTACTED]: '#52C41A',
          [CrashContactStatus.UNREACHABLE]: '#FF4D4F',
          [CrashContactStatus.NOT_CONTACTED]: '#FAAD14',
          [CrashContactStatus.NOT_APPLICABLE]: '#8C8C8C',
        }
        if (!opt) return '-'
        return (
          <Space>
            <Dot color={colorMap[currentStatus] || '#8C8C8C'} />
            {opt.name}
          </Space>
        )
      },
    },
    {
      title: 'วันและเวลากดยืนยัน',
      dataIndex: 'userRespondAt',
      key: 'userRespondAt',
      width: 250,
      className: UserSelectStyle,
      render: (text: string) => formatThaiBE(text),
    },
    {
      title: 'เลขที่กรมธรรม์',
      dataIndex: 'policyNumber',
      key: 'policyNumber',
      width: 180,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: 'ทะเบียนรถ',
      dataIndex: 'licensePlate',
      key: 'licensePlate',
      width: 140,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: 'ชื่อ-นามสกุล',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: 'เบอร์โทรศัพท์ติดต่อฉุกเฉิน',
      dataIndex: 'emergencyContactNo',
      key: 'emergencyContactNo',
      width: 180,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: 'ระยะเวลารอการติดต่อกลับ',
      key: 'elapsed',
      width: 200,
      className: UserSelectStyle,
      render: (_: any, record: any) => getElapsed(record),
    },
    {
      title: 'วันและเวลาที่เกิดเหตุ',
      dataIndex: 'incidentDateTime',
      key: 'incidentDateTime',
      width: 300,
      className: UserSelectStyle,
      render: (text: string, record: any) =>
        record.incidentDateTime ? formatThaiBE(text).replace(',', '') : '-',
    },
    {
      title: 'สถานะเหตุ',
      dataIndex: 'currentIncidentStatus',
      key: 'currentIncidentStatus',
      width: 160,
      className: UserSelectStyle,
      filters:
        queryFilterIncidentStatus.data?.map((o) => ({
          text: o.name,
          value: o.code ?? 'noStatus',
        })) || [],
      filteredValue: filters['currentIncidentStatus'] || null,
      render: (status: CrashIncidentStatus) => {
        const currentStatus = status || CrashIncidentStatus.NO_STATUS
        const opt = queryFilterIncidentStatus.data?.find(
          (o) => (o.code ?? 'noStatus') === currentStatus,
        )
        const colorMap: Record<string, string> = {
          [CrashIncidentStatus.NO_STATUS]: '#8C8C8C',
          [CrashIncidentStatus.CRASH]: '#FF4D4F',
          [CrashIncidentStatus.NO_CRASH]: '#52C41A',
          null: '#8C8C8C',
        }
        if (!opt) return '-'
        return (
          <Space>
            <Dot color={colorMap[currentStatus] || '#8C8C8C'} />
            {opt.name}
          </Space>
        )
      },
    },
    {
      title: 'ส่ง SURVEYOR',
      dataIndex: 'isSendSurveyor',
      key: 'isSendSurveyor',
      width: 160,
      className: UserSelectStyle,
      render: (sent: boolean) => {
        if (sent === undefined || sent === null) return '-'
        return (
          <Space>
            <Dot color={sent ? '#52C41A' : '#8C8C8C'} />
            {sent ? 'ส่ง' : 'ไม่ส่ง'}
          </Space>
        )
      },
    },
    {
      title: 'ชื่ออุปกรณ์',
      dataIndex: 'detectedDeviceName',
      key: 'detectedDeviceName',
      width: 160,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: 'วันและเวลาที่ส่ง NOTI',
      dataIndex: 'notificationSendAt',
      key: 'notificationSendAt',
      width: 200,
      className: UserSelectStyle,
      render: (text: string) => formatThaiBE(text),
    },
    {
      title: 'อัปเดตข้อมูลล่าสุดโดย',
      dataIndex: 'ownerName',
      key: 'lastUpdateUserId',
      width: 200,
      className: UserSelectStyle,
      filters:
        queryFilterUser.data?.map((u) => ({
          text: u.fullName,
          value: u.userId ?? 'null',
        })) || [],
      filteredValue: filters['lastUpdateUserId'] || null,
      render: (text: string) => text || '-',
    },
    {
      title: 'อัปเดตล่าสุด',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      className: UserSelectStyle,
      render: (text: string, record: any) => {
        const isHandled =
          record.currentContactStatus &&
          record.currentContactStatus !== CrashContactStatus.NOT_CONTACTED
        return isHandled && text ? formatThaiBE(text) : '-'
      },
    },
    {
      title: 'เลขรับแจ้ง',
      dataIndex: 'notifyNumber',
      key: 'notifyNumber',
      width: 160,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'note',
      key: 'note',
      width: 240,
      className: UserSelectStyle,
      render: (text: string) => text || '-',
    },
    {
      title: '',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      className: UserSelectStyle,
      render: (_: any, record: any) => {
        const canEdit = hasEditPermission
        return (
          <Space size="middle">
            {canEdit ? (
              <Tooltip title="แก้ไข">
                <ButtonIcon
                  src={IMAGE_URL.editIcon}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEditCase(record)
                  }}
                />
              </Tooltip>
            ) : (
              <EmptySpaceIcon />
            )}

            <Tooltip title="ดูรายละเอียด">
              <ButtonIcon
                src={IMAGE_URL.eyeIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(CONSTANT.ROUTES.CRASH_DETECTION_VIEW_PAGE.replace(':id', record.id))
                }}
              />
            </Tooltip>
          </Space>
        )
      },
    },
  ]

  return {
    data,
    isLoading,
    columns,
    inputValue,
    onSearch,
    onTableChange,
    isAutoUpdate,
    setIsAutoUpdate,
    setSelectedRowId,
    selectedRowId,
  }
}

export default useCrashDetectionTableHook
